import { cn } from "../cn";

export interface Step<T extends string> {
  id: T;
  title: string;
  /** True once this step has been answered. Drives the tick, not the order. */
  done?: boolean;
  /** Blocks the step until something earlier is answered. */
  disabled?: boolean;
}

export interface StepsProps<T extends string> {
  /** Names the sequence for a screen reader — "Building a reward". */
  label: string;
  steps: Step<T>[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}

/**
 * A sequence of questions, numbered.
 *
 * Deliberately not `Tabs`, though they share a visual family on purpose so a
 * reader recognises the affordance. Tabs are alternative views of one thing and
 * their order carries no meaning; steps are a sequence, they have a notion of
 * *done*, and the numbering is a claim that the second question depends on the
 * first. Rendering steps through a tablist would tell a screen-reader user that
 * order does not matter, which is the one thing this control exists to say.
 *
 * Every step stays reachable. A stepper that locks you out of step four until
 * you have re-answered one through three is a wizard, and a wizard is the wrong
 * shape for editing something that already exists — somebody changing a limit
 * should not walk through three questions to reach it.
 */
export function Steps<T extends string>({
  label,
  steps,
  active,
  onChange,
  className,
}: StepsProps<T>) {
  return (
    <nav
      aria-label={label}
      className={cn("flex flex-wrap gap-1 border-b border-rule", className)}
    >
      {steps.map((step, i) => {
        const on = step.id === active;
        return (
          <button
            key={step.id}
            type="button"
            aria-current={on ? "step" : undefined}
            disabled={step.disabled}
            onClick={() => {
              onChange(step.id);
            }}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 font-sans text-ui transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              "disabled:pointer-events-none disabled:opacity-50",
              on
                ? "border-accent font-semibold text-ink"
                : "border-transparent text-ink-2 hover:text-ink",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "grid size-5 place-items-center rounded-full text-micro font-semibold",
                on
                  ? "bg-accent text-accent-ink"
                  : step.done
                    ? "bg-positive-soft text-positive-text"
                    : // `ink-2`, not `ink-3`. On the raised fill the dimmest ink is 3.88:1 in
                      // the dark theme, and no darkening of the ramp fixes that without
                      // closing the gap to `ink-2` altogether — so this is the one place the
                      // palette pass moved a call site instead of a token.
                      "bg-surface-2 text-ink-2",
              )}
            >
              {i + 1}
            </span>
            {step.title}
          </button>
        );
      })}
    </nav>
  );
}
